import { AfterViewInit, Component, ElementRef, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { DataserviceService } from 'src/app/dataservice.service';

type PageKey =
  'prequal' |
  'info' |
  'qual' |
  'plans' |
  'equipment' |
  'install' |
  'pfee' |
  'payment' |
  'review' |
  'confirm';

@Component({
  selector: 'app-dish-flow',
  templateUrl: './dish-flow.component.html',
  styleUrl: './dish-flow.component.css'
})
export class DishFlowComponent implements AfterViewInit {

  currentPage: PageKey = 'prequal';
  pageHistory: PageKey[] = [];
  fullData: { [key: string]: any } = {};
  submitted = false;
  cartVisible = false;
  cartLoading = false;
  cartPricing: any[] = [];
  readonly orderedSteps: PageKey[] = ['prequal', 'info', 'qual', 'plans', 'equipment', 'install', 'pfee', 'payment', 'review', 'confirm'];
  readonly stepLabels: Record<PageKey, string> = {
    prequal: 'Gift Card',
    info: 'Customer Info',
    qual: 'Credit Check',
    plans: 'Plans',
    equipment: 'Equipment',
    install: 'Install Date',
    pfee: 'Processing Fee',
    payment: 'Payment',
    review: 'Review',
    confirm: 'Confirmation'
  };

  @ViewChild('flowStepsContainer', { static: false }) stepsContainer?: ElementRef<HTMLUListElement>;
  @ViewChildren('flowStep') stepItems?: QueryList<ElementRef<HTMLLIElement>>;

  constructor(
    private data: DataserviceService,
    private message: NzMessageService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const replayParam = params['replay'];
      const customerInfoParam = params['customerInfo'];
      const fromMap = params['fromMap'];

      const parsedCustomerInfo = this.safeParseCustomerInfo(customerInfoParam);

      const promoName = params['promoName'];
      const promoId = params['promoId'];
      const conversationId = params['conversationId'];
      const customerId = params['customerId'];

      const parsedPromoName = promoName === 'false' || promoName === 'null' ? null : promoName;
      const parsedPromoId = promoId === 'false' || promoId === 'null' ? null : promoId;

      if (replayParam === 'true') {
        this.fullData['qual'] = {
          ...(this.fullData['qual'] || {}),
          promoName: parsedPromoName,
          promoId: parsedPromoId
        };
        if (conversationId) {
          this.fullData['conversationId'] = conversationId;
        }
        if (customerId) {
          this.fullData['customerId'] = customerId;
        }
        if (parsedCustomerInfo) {
          this.fullData['customerInfo'] = parsedCustomerInfo;
        }
        this.fullData = { ...this.fullData };
        this.currentPage = 'info';
        this.scheduleScrollToActiveStep();
      } else if (replayParam === 'false') {
        if (conversationId) {
          this.fullData['conversationId'] = conversationId;
        }
        if (customerId) {
          this.fullData['customerId'] = customerId;
        }
        if (parsedCustomerInfo) {
          this.fullData['customerInfo'] = parsedCustomerInfo;
        }
        this.fullData = { ...this.fullData };
      } else if (fromMap === 'true') {
        this.fullData['customerInfo'] = {
          firstName: params['first'],
          lastName: params['last'],
          phone: (params['phone'] || '').replace(/\D/g, ''),
          address: params['street'],
          city: params['city'],
          state: params['state'],
          zip: params['zip']
        };
        this.fullData = { ...this.fullData };
      }
      this.scheduleScrollToActiveStep();
    });
  }

  ngAfterViewInit(): void {
    this.stepItems?.changes.subscribe(() => this.scheduleScrollToActiveStep());
    this.scheduleScrollToActiveStep();
  }

  get currentStepLabel(): string {
    return this.stepLabels[this.currentPage] ?? 'Dish Flow';
  }

  get currentStepIndex(): number {
    return this.orderedSteps.indexOf(this.currentPage);
  }

  get progressPercent(): number {
    const stepsCount = this.orderedSteps.length;
    if (stepsCount === 0) {
      return 0;
    }
    const position = this.currentStepIndex + 1;
    const clampedPosition = Math.max(0, Math.min(position, stepsCount));
    return Math.round((clampedPosition / stepsCount) * 100);
  }

  isStepComplete(step: PageKey): boolean {
    return this.orderedSteps.indexOf(step) < this.currentStepIndex;
  }

  isStepActive(step: PageKey): boolean {
    return step === this.currentPage;
  }

  get canShowCart(): boolean {
    return !!this.fullData?.['conversationId'] && this.currentPage !== 'prequal' && this.currentPage !== 'info' && this.currentPage !== 'qual' && this.currentPage !== 'review' && this.currentPage !== 'confirm';
  }

  private safeParseCustomerInfo(value: any): any | null {
    if (!value) {
      return null;
    }
    try {
      return JSON.parse(value);
    } catch (err) {
      console.error('Failed to parse customerInfo from query params', err);
      return null;
    }
  }

  goBack() {
    const id = this.message.loading('Going back...', { nzDuration: 0 }).messageId;
    setTimeout(() => {
      this.message.remove(id)
    }, 1000)
    if (this.submitted) {
      return;
    } 
    
    const prev = this.pageHistory.pop();
    if (prev) {
      this.currentPage = prev;
      this.scheduleScrollToActiveStep();
    }
  }


  setPage(next: PageKey) {
    const id = this.message.loading('Moving to next page...', { nzDuration: 0 }).messageId;

    this.pageHistory.push(this.currentPage);
    this.currentPage = next;
    this.scheduleScrollToActiveStep();

    setTimeout(() => {
      this.message.remove(id);
    }, 500)
  }

  handlePreQual(event: any) {
    if(event?.preQual) {
      this.fullData = { ...event };
    } else {
      this.fullData = { ...this.fullData};
    }

    this.setPage('info')
  }

  handleCustomerCreated(event: { customerInfo: any; customerId: string, scrubAddressResponse: any, authenticateCustomerResponse: any, conversationId: any }) {
    this.fullData = {
      ...this.fullData,
      customerInfo: {
        ...(this.fullData?.['customerInfo'] ?? {}), 
        ...event.customerInfo
      },
      customerId: event.customerId,
      scrubAddressResponse: event.scrubAddressResponse,
      authenticateCustomerResponse: event.authenticateCustomerResponse,
      conversationId: event.conversationId
    };

    this.setPage('qual');
  }

  handleQualResponse(event: any) {
    const { woId, ...qual } = event || {};

    this.fullData['qual'] = qual;
    this.fullData['woId'] = woId;
    
    this.setPage('plans');
  }

  handleChosenPlans(event: any) {
    this.fullData['plans'] = event;

    this.setPage('equipment');
  }

  handleEquipment(event: any) {
    this.fullData['equipment'] = event;

    this.setPage('install');
  }

  handleInstallData(event: any) {
    this.fullData['install'] = event;

    this.setPage('pfee');
  }

  handlePFeeData(event: any) {
    this.fullData['processingFee'] = event;

    this.setPage('payment');
  }

  handleCCData(event: any) {
    this.fullData['creditCard'] = event;

    this.setPage('review');
  }

  handleReview(event: any) {
    this.fullData['reviewData'] = event;
    
    this.setPage('confirm');
  }

  handleConfirm(event: any) {
    this.fullData = {};
    this.cartVisible = false;
    this.cartPricing = [];
    this.cartLoading = false;
  }

  openCart() {
    if (!this.fullData?.['conversationId']) {
      return;
    }

    this.cartVisible = true;
    this.cartLoading = true;

    this.data.postDataNoBodyDotBody({
      conversationId: this.fullData?.['conversationId'],
      autopay: this.fullData?.['processingFee']?.['autopay'],
      paperless: this.fullData?.['processingFee']?.['paperless']
    }, 'dish/calculatePricing').subscribe({
      next: (r: any) => {
        const pricingResponse = r?.data?.Response?.PriceSelectionsResponse?.PricingInformation?.PricingCategory;
        if (Array.isArray(pricingResponse)) {
          this.cartPricing = pricingResponse;
        } else if (pricingResponse) {
          this.cartPricing = [pricingResponse];
        } else {
          this.cartPricing = [];
        }
        this.cartLoading = false;
      },
      error: (err: any) => {
        console.error(err);
        this.cartPricing = [];
        this.cartLoading = false;
      }
    });
  }

  closeCart() {
    this.cartVisible = false;
  }

  private scheduleScrollToActiveStep(): void {
    if (typeof window === 'undefined') {
      this.scrollActiveStepIntoView();
      return;
    }
    window.requestAnimationFrame(() => this.scrollActiveStepIntoView());
  }

  private scrollActiveStepIntoView(): void {
    const container = this.stepsContainer?.nativeElement;
    const items = this.stepItems?.toArray() ?? [];
    const activeIndex = this.currentStepIndex;

    if (!container || !items.length || activeIndex < 0) {
      return;
    }

    const activeItem = items[activeIndex]?.nativeElement;
    if (!activeItem) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const activeRect = activeItem.getBoundingClientRect();
    const offset = activeRect.left - containerRect.left - (containerRect.width / 2) + (activeRect.width / 2);

    container.scrollTo({
      left: container.scrollLeft + offset,
      behavior: 'smooth'
    });
  }

}
